import { createContext, ReactNode, useEffect, useState } from "react";
import api from "../services/api";
import { useParams } from "react-router-dom";
import moment from "moment";
import { Loading } from "../components/Loading";

export type ShiftType = {
  id: number;
  timeStart: string;
  timeStart2: string;
  timeEnd: string;
  timeEnd2: string;
  weekday: number;
};

export type PluginspaceResponseType = {
  cnpj?: string;
  id: number;
  hash: string;
  name: string;
  type: string;
  typeId: number;
  gmt?: string;
};

type AddressLocationType = {
  cep: string;
  city: string;
  district: string;
  extra: string;
  number: string;
  state: string;
  street: string;
};

export type LocationType = {
  id: number;
  name: string;
  code: string;
  status: boolean;
  type: string;
  cep: string;
  address: AddressLocationType | null;
};

type PluginspaceContextType = {
  token: string;
  setToken: (token: string) => void;
  pluginspaceData: PluginspaceResponseType | undefined;
  setPluginspaceData: (pluginspaceData: PluginspaceResponseType | any) => void;
  getToken: (subdomain?: string) => Promise<string>;
  registerAccess: (
    pageName: string,
    cpfIndicator?: string,
    productIndicator?: number,
    urlBrowsed?: string
  ) => Promise<boolean>;
  getThisPluginspace: (
    otherToken?: string
  ) => Promise<void | PluginspaceResponseType>;
  getVerifyShift: () => Promise<boolean>;

  locations: LocationType[];
  getLocations: () => Promise<LocationType[] | any[]>;

  pluginspace: any;
  subDomain: string;
};

type PluginspaceContextProviderProps = {
  children: ReactNode;
};

export const PluginspaceContext = createContext({} as PluginspaceContextType);

export function PluginspaceContextProvider(
  props: PluginspaceContextProviderProps
) {
  const [token, setToken] = useState<string>("");
  const [pluginspaceData, setPluginspaceData] = useState();
  const [pluginspace, setPluginspace] = useState();
  const [shifts, setShifts] = useState<ShiftType[] | null>(null);
  const [subDomain, setSubDomain] = useState<string>("");
  const [locations, setLocations] = useState<LocationType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const subdomain = atob(window.location.pathname.split("/")[1]);
    setSubDomain(window.location.pathname.split("/")[1]);
    getToken(subdomain);
  }, []);

  async function getToken(subdomain?: string) {
    try {
      const currentSubdomain = subdomain || window.atob(window.location.pathname.split("/")[1]);

      // Verificar se token válido está em cache
      const cachedToken = getValidCachedToken(currentSubdomain);
      if (cachedToken) {
        return cachedToken;
      }

      // Token inválido ou expirado - limpar cache
      clearTokenCache();

      // Buscar novo token da API
      const newToken = await fetchNewToken(currentSubdomain);
      return newToken || '';
    } catch (error) {
      console.error('Error getting token:', error);
      clearTokenCache();
      return '';
    } finally {
      setIsLoading(false);
    }
  }

  // Função auxiliar para validar token em cache
  function getValidCachedToken(currentSubdomain: string): string | null {
    try {
      const tokenLocal = localStorage.getItem("tk_data");
      const expiryDate = localStorage.getItem("expiry_tk");

      if (!tokenLocal || !expiryDate) return null;

      const parsedToken = JSON.parse(tokenLocal);
      const isValidSubdomain = parsedToken?.subdomain === currentSubdomain;
      const isNotExpired = new Date(expiryDate).getTime() > new Date().getTime();

      if (isValidSubdomain && isNotExpired && parsedToken?.token) {
        return parsedToken.token;
      }

      return null;
    } catch (error) {
      console.warn('Error parsing cached token:', error);
      return null;
    }
  }

  // Função auxiliar para buscar novo token
  async function fetchNewToken(subdomain: string): Promise<string> {
    try {
      const response = await api.post(`/api/token/get-token`, { subdomain });

      if (response.data?.accessToken) {
        const { accessToken, pluginspace, expiratedAt } = response.data;

        // Atualizar estado
        setPluginspaceData(pluginspace);
        setToken(accessToken);

        // Guardar em cache
        localStorage.setItem(
          "tk_data",
          JSON.stringify({
            token: accessToken,
            subdomain: subdomain,
          })
        );
        localStorage.setItem("expiry_tk", expiratedAt);

        return accessToken;
      }

      throw new Error('No access token in response');
    } catch (error) {
      console.error('Error fetching new token:', error);
      throw error;
    }
  }

  // Função auxiliar para limpar cache
  function clearTokenCache(): void {
    const itemsToRemove = [
      "locations",
      "orderToVerify",
      "storeAddress",
      "this_pg",
      "should_show_ler_qrcodeButton",
      "qrcodePixPayment",
      "tk_data",
      "expiry_tk",
    ];

    itemsToRemove.forEach(item => localStorage.removeItem(item));
  }

  async function getThisPluginspace(otherToken?: string) {
    try {
      // Garante que temos um token
      const token = otherToken || await getToken();

      // Tenta recuperar dados do localStorage
      const cachedData = getCachedPluginspaceData();
      if (cachedData) {
        validateFoodToRedirect(cachedData)
        return cachedData;
      }

      // Busca dados da API se não estiver em cache
      return await fetchPluginspaceFromAPI(token);
    } catch (error) {
      console.error('Error getting pluginspace:', error);
      return null;
    }
  }


  // Função auxiliar para encoding seguro (suporta UTF-8)
  function safeBase64Encode(str: string): string {
    try {
      return btoa(unescape(encodeURIComponent(str)));
    } catch (error) {
      console.error('Error encoding string:', error);
      return '';
    }
  }

  // Função auxiliar para decoding seguro
  function safeBase64Decode(str: string): string {
    try {
      return decodeURIComponent(escape(atob(str)));
    } catch (error) {
      console.error('Error decoding string:', error);
      return '';
    }
  }

  // Função auxiliar para recuperar dados do cache
  function getCachedPluginspaceData() {
    try {
      const pluginspaceDataLocal = localStorage.getItem("this_pg");
      if (!pluginspaceDataLocal) return null;

      const pluginspaceData = JSON.parse(safeBase64Decode(pluginspaceDataLocal));

      // Verifica se o subdomain atual corresponde ao cached
      const currentSubdomain = window.location.pathname.split("/")[1];
      const cachedSubdomain = safeBase64Encode(pluginspaceData.subdomain);

      if (currentSubdomain === cachedSubdomain) {
        setPluginspace(pluginspaceData);
        return pluginspaceData;
      }

      return null;
    } catch (error) {
      // Se houver erro no parsing, remove cache inválido
      localStorage.removeItem("this_pg");
      return null;
    }
  }


  interface PluginspaceData {
    type?: string;
    [key: string]: any;
  }

  function validateFoodToRedirect(pluginspaceData: PluginspaceData): boolean {
    try {
      if (pluginspaceData && pluginspaceData.type === 'food') {
        const pathname = window.location.pathname;
        const isProd = process.env.REACT_APP_PROD === '1'
        // Base das URLs de destino (Ajuste para as suas variáveis reais de ambiente/config)
        const URL_WITH_PARAMS = isProd ? 'https://web-food-command.ninegrid.com.br/' : 'https://web-staging-food-command.ninegrid.com.br/'; // ex: xxx + card/123/mesa1
        const URL_DEFAULT = isProd ? 'https://web-food.ninegrid.com.br/' : 'https://web-staging-food.ninegrid.com.br/';      // ex: sem parâmetros

        const subDomain = window.location.pathname.split("/")[1];

        // Lista de padrões de rota organizados da mais específica para a mais genérica
        const routePatternsCommandWebApp = [
          /card\/[^\/]+\/[^\/]+/,           // card/:card_number/:card_name
          /card\/[^\/]+/,                   // card/:card_number
          /table\/[^\/]+\/[^\/]+/,          // table/:card_number/:table_name
          /table\/[^\/]+/,                  // table/:card_number
          /internal-delivery\/[^\/]+\/[^\/]+/, // internal-delivery/:internal_number/:internal_name
          /internal-delivery\/[^\/]+/,      // internal-delivery/:internal_number
          /pluginbot\/[^\/]+\/[^\/]+/,      // pluginbot/:landmark_pluginbot/:pluginbot_name
          /pluginbot\/[^\/]+/,              // pluginbot/:landmark_pluginbot
          /counter\/[^\/]+/                 // counter/:counter_number
        ];

        const RoutePatterns = [
          '/queue',
          '/booking'
        ];

        // Procura se alguma das rotas bate com a URL atual
        let matchedPathCommand: string | null = null;
        let matchedPathRoute: string | null = null;

        for (const pattern of routePatternsCommandWebApp) {
          const match = pathname.match(pattern);
          if (match) {
            matchedPathCommand = match[0]; // Captura o trecho como 'card/123/mario'
            break; // Para no primeiro match encontrado
          }
        }

        for (const route of RoutePatterns) {
          const match = pathname.match(route);
          if (match) {
            matchedPathRoute = match[0];
            break;
          }
        }

        // Redirecionamento
        if (matchedPathCommand) {
          // Possui parâmetros na URL -> Envia para URL com os parâmetros
          window.location.href = `${URL_WITH_PARAMS}${subDomain}/${matchedPathCommand}`;
        }
        else if (matchedPathRoute) {
          window.location.href = `${URL_DEFAULT}${subDomain}${matchedPathRoute}`;
        }
        else {
          // Não possui os parâmetros mapeados -> Envia para a URL padrão
          window.location.href = `${URL_DEFAULT}${subDomain}`;
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error validating food to redirect:', error);
      return false;
    }
  }

  // Função auxiliar para buscar dados da API
  async function fetchPluginspaceFromAPI(token: string) {
    try {
      const response = await api.get(`/api/pluginspace/this`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const pluginspaceData = response.data?.data?.[0];
      if (!pluginspaceData) return null;

      validateFoodToRedirect(pluginspaceData)

      // Salva no cache e atualiza estado
      localStorage.setItem("this_pg", safeBase64Encode(JSON.stringify(pluginspaceData)));
      setPluginspace(pluginspaceData);

      return pluginspaceData;
    } catch (error) {
      console.error('API request failed:', error);
      return null;
    }
  }

  async function registerAccess(
    pageName: string,
    cpfIndicator?: string,
    productIndicator?: number,
    urlBrowsed?: string
  ) {
    return await api
      .post(
        `/api/accessCount/registerAccess`,
        {
          pageName,
          cpfIndicator,
          productIndicator,
          urlBrowsed,
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then(async () => {
        return true;
      })
      .catch((err) => {
        return false;
      });
  }

  async function getVerifyShift() {
    if (shifts && shifts.length > 0) {
      return verifyShift(shifts);
    } else {
      try {
        const response = await api.get(`api/location-operation`, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        });
        if (response.data.data) {
          setShifts(response.data.data);
          return verifyShift(response.data.data);
        } else {
          return false;
        }
      } catch (error) {
        setShifts(null);
        return false;
      }
    }
  }

  function isTimeBetween(
    time: string,
    startTime: string,
    endTime: string
  ): boolean {
    const format = "HH:mm:ss";
    const timeMoment = moment(time, format);
    const startMoment = moment(startTime, format);
    const endMoment = moment(endTime, format);

    // Caso o intervalo atravesse a meia-noite
    if (endMoment.isBefore(startMoment)) {
      return (
        timeMoment.isBetween(startMoment, moment("23:59:59", format)) ||
        timeMoment.isBetween(moment("00:00:00", format), endMoment)
      );
    } else {
      return timeMoment.isBetween(startMoment, endMoment, null, "[]"); // Inclui os limites
    }
  }

  // Helper para extrair parte de tempo com segurança
  const getTimePart = (timeString: string | null | undefined, index: number, defaultValue = "0"): string => {
    if (!timeString || typeof timeString !== "string") return defaultValue;
    const parts = timeString.split(":");
    return parts[index] || defaultValue;
  };

  async function verifyShift(shiftsToVerify: ShiftType[]) {
    const pg = await getThisPluginspace();
    const shift = shiftsToVerify.find(
      (shiftItem) => shiftItem.weekday === new Date().getDay()
    );
    if (!shift) {
      return false;
    }
    let success = false;
    const gtmValue =
      !pg || !pg.gmt ? -3 : parseInt(pg.gmt.split(":")[0]);
    const now = moment();
    now.subtract(now.parseZone().utcOffset(), "minutes");

    // Validação segura dos campos de tempo do shift
    if (!shift.timeStart || !shift.timeStart2 || !shift.timeEnd || !shift.timeEnd2) {
      console.warn("Shift com campos de tempo inválidos:", shift);
      return false;
    }

    let timeStartHourGtm =
      parseInt(getTimePart(shift.timeStart, 0)) + gtmValue * -1;
    let timeStart = `${timeStartHourGtm > 9 ? timeStartHourGtm : `0${timeStartHourGtm}`
      }:${getTimePart(shift.timeStart, 1)}:${getTimePart(shift.timeStart, 2)}`;
    timeStart =
      parseInt(timeStart[0] + timeStart[1]) >= 24
        ? `${parseInt(timeStart[0] + timeStart[1]) - 24 > 9
          ? parseInt(timeStart[0] + timeStart[1]) - 24
          : `0${parseInt(timeStart[0] + timeStart[1]) - 24}`
        }:${getTimePart(shift.timeStart, 1)}:${getTimePart(shift.timeStart, 2)}`
        : timeStart;

    timeStartHourGtm = parseInt(getTimePart(shift.timeStart2, 0)) + gtmValue * -1;
    let timeStart2 = `${timeStartHourGtm > 9 ? timeStartHourGtm : `0${timeStartHourGtm}`
      }:${getTimePart(shift.timeStart2, 1)}:${getTimePart(shift.timeStart2, 2)}`;
    timeStart2 =
      parseInt(timeStart2[0] + timeStart2[1]) >= 24
        ? `${parseInt(timeStart2[0] + timeStart2[1]) - 24 > 9
          ? parseInt(timeStart2[0] + timeStart2[1]) - 24
          : `0${parseInt(timeStart2[0] + timeStart2[1]) - 24}`
        }:${getTimePart(shift.timeStart2, 1)}:${getTimePart(shift.timeStart2, 2)}`
        : timeStart2;

    timeStartHourGtm = parseInt(getTimePart(shift.timeEnd, 0)) + gtmValue * -1;
    let timeEnd = `${timeStartHourGtm > 9 ? timeStartHourGtm : `0${timeStartHourGtm}`
      }:${getTimePart(shift.timeEnd, 1)}:${getTimePart(shift.timeEnd, 2)}`;
    timeEnd =
      parseInt(timeEnd[0] + timeEnd[1]) >= 24
        ? `${parseInt(timeEnd[0] + timeEnd[1]) - 24 > 9
          ? parseInt(timeEnd[0] + timeEnd[1]) - 24
          : `0${parseInt(timeEnd[0] + timeEnd[1]) - 24}`
        }:${getTimePart(shift.timeEnd, 1)}:${getTimePart(shift.timeEnd, 2)}`
        : timeEnd;

    timeStartHourGtm = parseInt(getTimePart(shift.timeEnd2, 0)) + gtmValue * -1;
    let timeEnd2 = `${timeStartHourGtm > 9 ? timeStartHourGtm : `0${timeStartHourGtm}`
      }:${getTimePart(shift.timeEnd2, 1)}:${getTimePart(shift.timeEnd2, 2)}`;
    timeEnd2 =
      parseInt(timeEnd2[0] + timeEnd2[1]) >= 24
        ? `${parseInt(timeEnd2[0] + timeEnd2[1]) - 24 > 9
          ? parseInt(timeEnd2[0] + timeEnd2[1]) - 24
          : `0${parseInt(timeEnd2[0] + timeEnd2[1]) - 24}`
        }:${getTimePart(shift.timeEnd2, 1)}:${getTimePart(shift.timeEnd2, 2)}`
        : timeEnd2;
    const nowTime = `${now.hours() > 9 ? now.hours() : `0${now.hours()}`}:${now.minutes() > 9 ? now.minutes() : `0${now.minutes()}`
      }:00`;

    if (
      moment(nowTime, "hh:mm:ss").isBetween(
        moment(timeStart, "hh:mm:ss"),
        moment(timeEnd, "hh:mm:ss")
      ) ||
      isTimeBetween(nowTime, timeStart, timeEnd)
    ) {
      success = true;
    } else if (
      moment(nowTime, "hh:mm:ss").isBetween(
        moment(timeStart2, "hh:mm:ss"),
        moment(timeEnd2, "hh:mm:ss")
      ) ||
      isTimeBetween(nowTime, timeStart2, timeEnd2)
    ) {
      success = true;
    } else if (
      parseInt(timeEnd[0] + timeEnd[1]) >= 0 &&
      parseInt(timeStart[0] + timeStart[1]) >= parseInt(timeEnd[0] + timeEnd[1])
    ) {
      if (
        moment(nowTime, "hh:mm:ss").isBetween(
          moment(timeStart, "hh:mm:ss"),
          moment("23:59:59", "hh:mm:ss"),
          "minutes"
        )
      ) {
        success = true;
      } else if (
        moment(nowTime, "hh:mm:ss").isBetween(
          moment("00:00:00", "hh:mm:ss"),
          moment(timeEnd, "hh:mm:ss"),
          "minutes"
        )
      ) {
        success = true;
      }
    } else if (
      parseInt(timeEnd2[0] + timeEnd2[1]) >= 0 &&
      parseInt(timeStart2[0] + timeStart2[1]) >=
      parseInt(timeEnd2[0] + timeEnd2[1])
    ) {
      if (
        moment(nowTime, "hh:mm:ss").isBetween(
          moment(timeStart2, "hh:mm:ss"),
          moment("00:00:00", "hh:mm:ss")
        )
      ) {
        success = true;
      } else if (
        moment(nowTime, "hh:mm:ss").isBetween(
          moment("00:00:00", "hh:mm:ss"),
          moment(timeEnd2, "hh:mm:ss")
        )
      ) {
        success = true;
      }
    }
    return success;
  }

  async function getLocations() {
    if (locations && locations.length > 0) {
      return locations;
    }

    return await api
      .get(`/api/location`, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response) => {
        const newLocations = response.data.data.map(
          (location: LocationType) => ({
            id: location.id,
            name: location.name,
            code: location.code,
            status: location.status,
            type: location.type,
            cep: location.cep,
            address: location.address,
          })
        );
        const newLocationsFiltered = newLocations.filter(
          (loc: LocationType) => loc.status === true
        );
        setLocations(newLocationsFiltered);

        localStorage.setItem("locations", JSON.stringify(newLocationsFiltered));

        return newLocationsFiltered;
      })
      .catch((err) => {
        console.log(err);
        return [];
      });
  }

  async function getData() {
    await Promise.all([getLocations(), getThisPluginspace()]);
  }

  useEffect(() => {
    getData();
  }, []);


  const contextValue = {
    token,
    setToken,
    pluginspaceData,
    setPluginspaceData,
    getToken,
    registerAccess,
    getThisPluginspace,
    getVerifyShift,
    getLocations,
    locations,
    pluginspace,
    subDomain,
  };

  if (isLoading) {
    return (
      <PluginspaceContext.Provider
        value={contextValue}
      >
        <div style={{
          height: '100vh'
        }}>
          <Loading
            type="spinningBubbles"
            color="#FFF"
            width={64}
            height={64}
          />
        </div>
      </PluginspaceContext.Provider>

    )
  }

  return (
    <PluginspaceContext.Provider
      value={contextValue}
    >
      {props.children}
    </PluginspaceContext.Provider>
  );
}
